/**
 * Modal Component
 * 
 * Reusable modal dialog system for Import/Export, Optimizer, Help, etc.
 */

export class Modal {
    constructor(id, title) {
        this.id = id;
        this.title = title;
        this.isOpen = false;
        this.onClose = null;
        
        this.createModalElement();
    }
    
    /**
     * Create modal DOM element
     */
    createModalElement() {
        const modal = document.createElement('div');
        modal.id = this.id;
        modal.className = 'modal';
        modal.innerHTML = `
            <div class="modal-overlay"></div>
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="modal-title">${this.title}</h2>
                    <button class="modal-close" aria-label="Close">&times;</button>
                </div>
                <div class="modal-body"></div>
            </div>
        `;
        
        document.body.appendChild(modal);
        this.element = modal;
        this.overlay = modal.querySelector('.modal-overlay');
        this.content = modal.querySelector('.modal-content');
        this.body = modal.querySelector('.modal-body');
        
        // Setup close handlers
        const closeBtn = modal.querySelector('.modal-close');
        closeBtn.addEventListener('click', () => this.close());
        
        this.overlay.addEventListener('click', () => this.close());
        
        // Close on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });
        
        // Initially hidden
        this.close();
    }
    
    /**
     * Set modal body content
     */
    setContent(html) {
        this.body.innerHTML = html;
    }
    
    /**
     * Open the modal
     */
    open() {
        this.isOpen = true;
        this.element.classList.add('open');
        document.body.style.overflow = 'hidden';
    }
    
    /**
     * Close the modal
     */
    close() {
        this.isOpen = false;
        this.element.classList.remove('open');
        document.body.style.overflow = '';
        
        if (this.onClose) {
            this.onClose();
        }
    }
    
    /**
     * Remove modal from DOM
     */
    destroy() {
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }
    }
}

/**
 * Import/Export Modal
 */
export class ImportExportModal extends Modal {
    constructor() {
        super('import-export-modal', 'Share City');
        
        this.onImport = null;
        this.onExport = null;
        this.selectedTerrain = 'land'; // Default to land
        this.isLoggedIn = false;
        this.currentUsername = null;
        this.cachedLayouts = []; // Cache for imported layouts
        this.maxCacheSize = 50; // Maximum number of cached layouts
        
        this.render();
        this.checkAuthStatus();
        this.loadCachedLayouts();
    }
    
    /**
     * Check if user is logged in
     */
    async checkAuthStatus() {
        try {
            // TODO: Update to use new Node.js API when auth is implemented
            // For now, gracefully handle missing auth endpoint
            const basePath = this.getBasePath();
            const apiBase = `${basePath}api/auth/check.php`;
            const response = await fetch(apiBase);
            
            // Check if response is JSON before parsing
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                // Not JSON (probably HTML 404 page) - treat as not logged in
                this.isLoggedIn = false;
                this.currentUsername = null;
                return;
            }
            
            const data = await response.json();
            this.isLoggedIn = data.loggedIn || false;
            this.currentUsername = data.user ? data.user.username : null;
            
            // Show/hide bulk upload tab based on login status
            const bulkTab = document.querySelector('.main-tab-btn[data-tab="bulk"]');
            if (bulkTab) {
                if (!this.isLoggedIn) {
                    bulkTab.style.display = 'none';
                } else {
                    bulkTab.style.display = 'block';
                }
            }
        } catch (error) {
            // Silently fail - auth endpoint may not exist yet
            this.isLoggedIn = false;
            this.currentUsername = null;
            const bulkTab = document.querySelector('.main-tab-btn[data-tab="bulk"]');
            if (bulkTab) {
                bulkTab.style.display = 'none';
            }
        }
    }
    
    render() {
        this.setContent(`
            <div class="import-export-content">
                <!-- Main Tabs: Import, Bulk Upload, and Export/Share -->
                <div class="main-tabs" style="display: flex; gap: 8px; margin-bottom: 20px; border-bottom: 1px solid rgba(255, 255, 255, 0.1);">
                    <button type="button" class="main-tab-btn active" data-tab="import" style="padding: 10px 20px; background: transparent; border: none; border-bottom: 2px solid var(--accent-color); color: white; cursor: pointer; font-size: 14px; font-weight: 600;">
                        Import
                    </button>
                    <button type="button" class="main-tab-btn" data-tab="bulk" style="padding: 10px 20px; background: transparent; border: none; border-bottom: 2px solid transparent; color: rgba(255, 255, 255, 0.6); cursor: pointer; font-size: 14px; font-weight: 600;">
                        Bulk Upload
                    </button>
                    <button type="button" class="main-tab-btn" data-tab="export" style="padding: 10px 20px; background: transparent; border: none; border-bottom: 2px solid transparent; color: rgba(255, 255, 255, 0.6); cursor: pointer; font-size: 14px; font-weight: 600;">
                        Export/Share
                    </button>
                </div>
                
                <!-- Import Section -->
                <div class="import-section" id="main-import-section">
                    <h3>Import City</h3>
                    <p>Paste a ShareString, ShareLink, Short Link, or URL to load a city:</p>
                    
                    <!-- Terrain Selector -->
                    <div class="import-terrain-selector" style="margin-bottom: 12px;">
                        <label style="display: block; margin-bottom: 6px; font-weight: 600;">Terrain Type:</label>
                        <div style="display: flex; gap: 12px;">
                            <label style="display: flex; align-items: center; gap: 6px; cursor: pointer;">
                                <input type="radio" name="import-terrain" value="land" checked style="cursor: pointer;">
                                <span>Land</span>
                            </label>
                            <label style="display: flex; align-items: center; gap: 6px; cursor: pointer;">
                                <input type="radio" name="import-terrain" value="water" style="cursor: pointer;">
                                <span>Water</span>
                            </label>
                        </div>
                    </div>
                    
                    <!-- Note about City Layout -->
                    <div class="import-note" style="background: rgba(255, 255, 255, 0.05); border-left: 3px solid var(--accent-color); padding: 10px; margin-bottom: 12px; font-size: 12px; line-height: 1.5;">
                        <strong>Note:</strong> If you select your layout from "City Layout" and not "Layout Planner", please select one of the options above as the "City Layout" no longer differentiates between Land and Water.
                    </div>
                    
                    <!-- Combined Input Field -->
                    <div style="margin-bottom: 20px;">
                        <label style="display: block; margin-bottom: 6px; font-weight: 600; font-size: 13px;">Paste link or code:</label>
                        <textarea id="import-input" class="import-textarea" 
                            placeholder="Paste any format: ShareString, ShareLink, ShortLink, ShortCode, or URL (e.g., J0cs or http://cotgopt.com/J0cs)"></textarea>
                        <small style="display: block; margin-top: 6px; font-size: 11px; color: rgba(255, 255, 255, 0.6);">
                            Supports: ShareString, ShareLink (W/L), ShortLink (1-9 chars), ShortCode (4 chars), or full URLs
                        </small>
                    </div>
                    
                    <!-- Decoding Method Selector (for ShortLinks) -->
                    <div class="decode-method-selector" style="margin-bottom: 12px; display: none;" id="decode-method-section">
                        <label style="display: block; margin-bottom: 6px; font-weight: 600;">Decoding Method (for ShortLinks):</label>
                        <div style="display: flex; gap: 12px;">
                            <label style="display: flex; align-items: center; gap: 6px; cursor: pointer;">
                                <input type="radio" name="decode-method" value="og-cotg" checked style="cursor: pointer;">
                                <span>OG-CoTG (New)</span>
                            </label>
                            <label style="display: flex; align-items: center; gap: 6px; cursor: pointer;">
                                <input type="radio" name="decode-method" value="lou" style="cursor: pointer;">
                                <span>Lou (Old)</span>
                            </label>
                        </div>
                        <small style="display: block; margin-top: 4px; font-size: 11px; color: rgba(255, 255, 255, 0.6);">
                            Only needed for ShortLinks. Try "OG-CoTG" first (default), then "Lou" if not found.
                        </small>
                    </div>
                    
                    <button id="btn-import" class="btn-primary" style="margin-top: 12px;">Import</button>
                    
                    <!-- Cached Layouts Dropdown -->
                    <div id="cached-layouts-section" style="display: none; margin-top: 20px; padding-top: 20px; border-top: 1px solid rgba(255, 255, 255, 0.1);">
                        <h4 style="margin-bottom: 12px; font-size: 16px;">Cached Layouts</h4>
                        <p style="font-size: 13px; color: rgba(255, 255, 255, 0.7); margin-bottom: 12px;">
                            Quick access to recently imported layouts:
                        </p>
                        <div style="display: flex; gap: 10px; align-items: center;">
                            <select id="cached-layouts-dropdown" 
                                style="flex: 1; padding: 8px; border: 1px solid rgba(255, 255, 255, 0.2); background: rgba(0, 0, 0, 0.3); color: white; border-radius: 4px; font-size: 13px;">
                                <option value="">Select a cached layout...</option>
                            </select>
                            <button id="btn-load-cached" class="btn-primary" style="padding: 8px 16px;">
                                Load
                            </button>
                            <button id="btn-compare-layouts" class="btn-primary" style="padding: 8px 16px; background: rgba(199, 168, 107, 0.2); border: 1px solid rgba(199, 168, 107, 0.4);">
                                Compare
                            </button>
                            <button id="btn-clear-cache" class="btn-primary" style="padding: 8px 16px; background: rgba(179, 67, 67, 0.2); border: 1px solid rgba(179, 67, 67, 0.4);">
                                Clear Cache
                            </button>
                        </div>
                        <div style="margin-top: 10px; display: flex; gap: 10px;">
                            <select id="cached-layouts-dropdown-2" 
                                style="flex: 1; padding: 8px; border: 1px solid rgba(255, 255, 255, 0.2); background: rgba(0, 0, 0, 0.3); color: white; border-radius: 4px; font-size: 13px;">
                                <option value="">Select second layout for comparison...</option>
                            </select>
                        </div>
                    </div>
                    
                </div>
                
                <!-- Bulk Upload Section -->
                <div class="bulk-upload-section" id="main-bulk-section" style="display: none;">
                    <h3>Bulk Upload Layouts</h3>
                    <p style="font-size: 13px; color: rgba(255, 255, 255, 0.7); margin-bottom: 15px;">
                        Upload multiple layouts from spreadsheets (Google Sheets, Excel, etc.). Paste one layout per line (ShareString, ShareLink, ShortLink, ShortCode, or URL):
                    </p>
                    
                    <div class="import-note" style="background: rgba(255, 255, 255, 0.05); border-left: 3px solid var(--accent-color); padding: 10px; margin-bottom: 12px; font-size: 12px; line-height: 1.5;">
                        <strong>Tip:</strong> Copy a column from your spreadsheet and paste it here. Each line will be processed as a separate layout.
                    </div>
                    
                    <div class="import-note" style="background: rgba(255, 255, 255, 0.05); border-left: 3px solid var(--accent-color); padding: 10px; margin-bottom: 12px; font-size: 12px; line-height: 1.5;">
                        <strong>Example format:</strong><br>
                        <code style="display: block; margin-top: 6px; padding: 8px; background: rgba(0, 0, 0, 0.3); border-radius: 4px; font-size: 11px; white-space: pre-wrap; overflow-x: auto;">
[ShareString.1.3];...<br>
LABC123...<br>
J0cs<br>
http://cotgopt.com/api/maps/short/aB3c
                        </code>
                        <div style="margin-top: 8px;">
                            Each line will be processed as a separate layout. You can mix different formats (ShareString, ShareLink, ShortLink, ShortCode, or URLs).
                        </div>
                    </div>
                    
                    <textarea id="bulk-import-input" class="import-textarea" 
                        placeholder="Paste multiple layouts here, one per line..."
                        style="width: 100%; min-height: 200px; padding: 10px; border: 1px solid rgba(255, 255, 255, 0.2); background: rgba(0, 0, 0, 0.3); color: white; border-radius: 4px; font-size: 12px; font-family: monospace; resize: vertical;"></textarea>
                    
                    <!-- Options -->
                    <div style="margin-top: 15px; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 4px;">
                        <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px;">
                            <input type="checkbox" id="bulk-set-favorites" style="cursor: pointer; width: 18px; height: 18px;">
                            <span><strong>Set these to Favorites</strong> (for layouts already in database)</span>
                        </label>
                        <small style="display: block; margin-top: 6px; margin-left: 26px; font-size: 11px; color: rgba(255, 255, 255, 0.6);">
                            If a layout already exists in the database, it will be added to your favorites instead of creating a duplicate.
                        </small>
                    </div>
                    
                    <div style="display: flex; gap: 10px; margin-top: 15px;">
                        <button id="btn-bulk-import" class="btn-primary" style="flex: 1;">
                            Import & Save All
                        </button>
                        <button id="btn-bulk-import-only" class="btn-primary" style="flex: 1; background: rgba(199, 168, 107, 0.2); border: 1px solid rgba(199, 168, 107, 0.4);">
                            Import Only (Don't Save)
                        </button>
                    </div>
                    
                    <div id="bulk-import-progress" style="display: none; margin-top: 15px; padding: 10px; background: rgba(255, 255, 255, 0.05); border-radius: 4px;">
                        <div style="font-size: 12px; margin-bottom: 8px;">
                            <span id="bulk-import-status">Processing...</span>
                        </div>
                        <div style="width: 100%; height: 6px; background: rgba(0, 0, 0, 0.3); border-radius: 3px; overflow: hidden;">
                            <div id="bulk-import-progress-bar" style="height: 100%; background: var(--accent-color); width: 0%; transition: width 0.3s;"></div>
                        </div>
                        <div id="bulk-import-results" style="margin-top: 10px; font-size: 11px; max-height: 200px; overflow-y: auto;"></div>
                    </div>
                </div>
                
                <!-- Export Section -->
                <div class="export-section" id="main-export-section" style="display: none;">
                    <h3>Export City</h3>
                    <p>Copy your city layout to share:</p>
                    
                    <!-- ShareString and ShareLink Side by Side -->
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px;">
                        <!-- ShareString -->
                        <div>
                            <label style="display: block; margin-bottom: 6px; font-weight: 600; font-size: 13px;">ShareString:</label>
                            <textarea id="export-sharestring" class="export-textarea" readonly 
                                style="width: 100%; min-height: 200px; padding: 10px; border: 1px solid rgba(255, 255, 255, 0.2); background: rgba(0, 0, 0, 0.3); color: white; border-radius: 4px; font-size: 12px; font-family: monospace; resize: vertical; text-align: center;"></textarea>
                            <button class="btn-copy-format" data-target="export-sharestring" 
                                style="margin-top: 8px; padding: 6px 12px; background: rgba(199, 168, 107, 0.2); border: 1px solid rgba(199, 168, 107, 0.4); color: var(--bronze-200); border-radius: 4px; cursor: pointer; font-size: 12px; width: 100%;">
                                Copy ShareString
                            </button>
                        </div>
                        
                        <!-- ShareLink -->
                        <div>
                            <label style="display: block; margin-bottom: 6px; font-weight: 600; font-size: 13px;">ShareLink:</label>
                            <textarea id="export-sharelink" class="export-textarea" readonly 
                                style="width: 100%; min-height: 200px; padding: 10px; border: 1px solid rgba(255, 255, 255, 0.2); background: rgba(0, 0, 0, 0.3); color: white; border-radius: 4px; font-size: 12px; font-family: monospace; resize: vertical;"></textarea>
                            <button class="btn-copy-format" data-target="export-sharelink" 
                                style="margin-top: 8px; padding: 6px 12px; background: rgba(199, 168, 107, 0.2); border: 1px solid rgba(199, 168, 107, 0.4); color: var(--bronze-200); border-radius: 4px; cursor: pointer; font-size: 12px; width: 100%;">
                                Copy ShareLink
                            </button>
                        </div>
                    </div>
                    
                    <!-- ShortLink and ShortCode (if available from database) -->
                    <div id="shortlink-shortcode-section" style="display: none; margin-bottom: 20px;">
                        <h4 style="margin-bottom: 12px; font-size: 16px;">Database Links</h4>
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
                            <!-- ShortLink -->
                            <div id="export-shortlink-container" style="display: none;">
                                <label style="display: block; margin-bottom: 6px; font-weight: 600; font-size: 13px;">ShortLink:</label>
                                <div style="display: flex; align-items: center; gap: 8px; padding: 10px; background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 4px;">
                                    <input type="text" id="export-shortlink" readonly 
                                        style="flex: 1; padding: 8px; border: none; background: transparent; color: white; font-size: 12px; font-family: monospace; outline: none;">
                                    <button class="btn-copy-format" data-target="export-shortlink" 
                                        style="padding: 6px 12px; background: rgba(199, 168, 107, 0.2); border: 1px solid rgba(199, 168, 107, 0.4); color: var(--bronze-200); border-radius: 4px; cursor: pointer; font-size: 12px; white-space: nowrap;">
                                        Copy
                                    </button>
                                </div>
                            </div>
                            
                            <!-- ShortCode -->
                            <div id="export-shortcode-container" style="display: none;">
                                <label style="display: block; margin-bottom: 6px; font-weight: 600; font-size: 13px;">ShortCode:</label>
                                <div style="display: flex; align-items: center; gap: 8px; padding: 10px; background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 4px;">
                                    <input type="text" id="export-shortcode" readonly 
                                        style="flex: 1; padding: 8px; border: none; background: transparent; color: white; font-size: 12px; font-family: monospace; outline: none;">
                                    <button class="btn-copy-format" data-target="export-shortcode" 
                                        style="padding: 6px 12px; background: rgba(199, 168, 107, 0.2); border: 1px solid rgba(199, 168, 107, 0.4); color: var(--bronze-200); border-radius: 4px; cursor: pointer; font-size: 12px; white-space: nowrap;">
                                        Copy
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Additional Information (if available) -->
                    <div id="additional-info-section" style="display: none; margin-bottom: 20px; padding: 15px; background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 8px;">
                        <h4 style="margin-bottom: 12px; font-size: 16px;">Additional Information</h4>
                        <div id="additional-info-content" style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; font-size: 13px;">
                            <!-- Will be populated dynamically -->
                        </div>
                    </div>
                    
                    <!-- Database URL Formats Section -->
                    <div id="database-urls-section" class="database-urls-section" style="margin-top: 20px; padding-top: 20px; border-top: 1px solid rgba(255, 255, 255, 0.1); display: none;">
                        <h4 style="margin-bottom: 12px; font-size: 16px;">Database URL Formats</h4>
                        <p style="font-size: 13px; color: rgba(255, 255, 255, 0.7); margin-bottom: 15px;">
                            This layout exists in the database. Copy any of these URL formats:
                        </p>
                        <div class="url-formats-list" style="display: flex; flex-direction: column; gap: 10px;">
                            <!-- Short Link URL -->
                            <div id="url-shortlink" class="url-format-item" style="display: none;">
                                <label style="display: block; margin-bottom: 4px; font-size: 12px; font-weight: 600; color: rgba(255, 255, 255, 0.8);">Short Link URL:</label>
                                <div class="url-copyable" style="display: flex; align-items: center; gap: 8px; padding: 8px; background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 4px; cursor: pointer; transition: all 0.2s;">
                                    <span class="url-text" style="flex: 1; font-family: monospace; font-size: 12px; color: white; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"></span>
                                    <button class="url-copy-btn" style="background: none; border: none; color: rgba(255, 255, 255, 0.7); cursor: pointer; font-size: 14px; padding: 4px 8px; flex-shrink: 0;" title="Copy to clipboard">📋</button>
                                </div>
                            </div>
                            <!-- Short Link Code -->
                            <div id="url-shortlink-code" class="url-format-item" style="display: none;">
                                <label style="display: block; margin-bottom: 4px; font-size: 12px; font-weight: 600; color: rgba(255, 255, 255, 0.8);">Short Link Code:</label>
                                <div class="url-copyable" style="display: flex; align-items: center; gap: 8px; padding: 8px; background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 4px; cursor: pointer; transition: all 0.2s;">
                                    <span class="url-text" style="flex: 1; font-family: monospace; font-size: 12px; color: white; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"></span>
                                    <button class="url-copy-btn" style="background: none; border: none; color: rgba(255, 255, 255, 0.7); cursor: pointer; font-size: 14px; padding: 4px 8px; flex-shrink: 0;" title="Copy to clipboard">📋</button>
                                </div>
                            </div>
                            <!-- Long Link URL -->
                            <div id="url-longlink" class="url-format-item" style="display: none;">
                                <label style="display: block; margin-bottom: 4px; font-size: 12px; font-weight: 600; color: rgba(255, 255, 255, 0.8);">Long Link:</label>
                                <div class="url-copyable" style="display: flex; align-items: center; gap: 8px; padding: 8px; background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 4px; cursor: pointer; transition: all 0.2s;">
                                    <span class="url-text" style="flex: 1; font-family: monospace; font-size: 12px; color: white; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"></span>
                                    <button class="url-copy-btn" style="background: none; border: none; color: rgba(255, 255, 255, 0.7); cursor: pointer; font-size: 14px; padding: 4px 8px; flex-shrink: 0;" title="Copy to clipboard">📋</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Submit to Layout Database Section -->
                    <div class="submit-layout-section" style="margin-top: 20px; padding-top: 20px; border-top: 1px solid rgba(255, 255, 255, 0.1);">
                        <h4 style="margin-bottom: 12px; font-size: 16px;">Submit to Layout Database</h4>
                        <p style="font-size: 13px; color: rgba(255, 255, 255, 0.7); margin-bottom: 15px;">
                            Save this layout to the database so others can find and use it!
                        </p>
                        
                        <div class="submit-layout-form" style="display: flex; flex-direction: column; gap: 12px;">
                            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                                <div>
                                    <label style="display: block; margin-bottom: 4px; font-size: 13px; font-weight: 600;">
                                        Layout Name *
                                    </label>
                                    <input type="text" id="submit-layout-name" 
                                        placeholder="e.g., RT Defense City" 
                                        style="width: 100%; padding: 8px; border: 1px solid rgba(255, 255, 255, 0.2); background: rgba(0, 0, 0, 0.3); color: white; border-radius: 4px; font-size: 13px;">
                                    <small style="font-size: 11px; color: rgba(255, 255, 255, 0.6);">Be descriptive!</small>
                                </div>
                                <div>
                                    <label style="display: block; margin-bottom: 4px; font-size: 13px; font-weight: 600;">
                                        Creator
                                    </label>
                                    <input type="text" id="submit-layout-creator" 
                                        placeholder="Your name" 
                                        style="width: 100%; padding: 8px; border: 1px solid rgba(255, 255, 255, 0.2); background: rgba(0, 0, 0, 0.3); color: white; border-radius: 4px; font-size: 13px;">
                                    <small style="font-size: 11px; color: rgba(255, 255, 255, 0.6);">That's your name!</small>
                                </div>
                            </div>
                            
                            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                                <div>
                                    <label style="display: block; margin-bottom: 4px; font-size: 13px; font-weight: 600;">
                                        Short Link
                                    </label>
                                    <input type="text" id="submit-layout-shortlink" 
                                        placeholder="aB3cD9xYz" 
                                        maxlength="9"
                                        style="width: 100%; padding: 8px; border: 1px solid rgba(255, 255, 255, 0.2); background: rgba(0, 0, 0, 0.3); color: white; border-radius: 4px; font-size: 13px;">
                                    <small style="font-size: 11px; color: rgba(255, 255, 255, 0.6);">9 chars, case-sensitive (optional - auto-generated if blank)</small>
                                </div>
                                <div>
                                    <label style="display: block; margin-bottom: 4px; font-size: 13px; font-weight: 600;">
                                        Map Type
                                    </label>
                                    <input type="text" id="submit-layout-maptype" 
                                        placeholder="RT, Vanq, Defense, etc." 
                                        style="width: 100%; padding: 8px; border: 1px solid rgba(255, 255, 255, 0.2); background: rgba(0, 0, 0, 0.3); color: white; border-radius: 4px; font-size: 13px;">
                                    <small style="font-size: 11px; color: rgba(255, 255, 255, 0.6);">Build type</small>
                                </div>
                            </div>
                            
                            <div>
                                <label style="display: block; margin-bottom: 4px; font-size: 13px; font-weight: 600;">
                                    WaLa
                                </label>
                                <select id="submit-layout-wala" 
                                    style="width: 100%; padding: 8px; border: 1px solid rgba(255, 255, 255, 0.2); background: rgba(0, 0, 0, 0.3); color: white; border-radius: 4px; font-size: 13px;">
                                    <option value="">Select...</option>
                                    <option value="Water">Water</option>
                                    <option value="Land">Land</option>
                                </select>
                                <small style="font-size: 11px; color: rgba(255, 255, 255, 0.6);">Water or Land?</small>
                            </div>
                            
                            <div id="submit-layout-message" style="display: none; padding: 10px; border-radius: 4px; font-size: 13px; margin-top: 5px;"></div>
                            
                            <button id="btn-submit-layout" class="btn-primary" style="margin-top: 5px;">
                                Submit to Database
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `);
        
        this.setupEventListeners();
    }
    
    setupEventListeners() {
        // Main tabs (Import and Export/Share)
        const mainTabs = document.querySelectorAll('.main-tab-btn');
        mainTabs.forEach(tab => {
            tab.addEventListener('click', async () => {
                const tabType = tab.dataset.tab;
                
                // Update tab styles
                mainTabs.forEach(t => {
                    t.classList.remove('active');
                    t.style.borderBottomColor = 'transparent';
                    t.style.color = 'rgba(255, 255, 255, 0.6)';
                });
                tab.classList.add('active');
                tab.style.borderBottomColor = 'var(--accent-color)';
                tab.style.color = 'white';
                
                // Show/hide sections
                const importSection = document.getElementById('main-import-section');
                const bulkSection = document.getElementById('main-bulk-section');
                const exportSection = document.getElementById('main-export-section');
                
                if (tabType === 'import') {
                    if (importSection) importSection.style.display = 'block';
                    if (bulkSection) bulkSection.style.display = 'none';
                    if (exportSection) exportSection.style.display = 'none';
                } else if (tabType === 'bulk') {
                    if (importSection) importSection.style.display = 'none';
                    if (bulkSection) bulkSection.style.display = 'block';
                    if (exportSection) exportSection.style.display = 'none';
                } else if (tabType === 'export') {
                    if (importSection) importSection.style.display = 'none';
                    if (bulkSection) bulkSection.style.display = 'none';
                    if (exportSection) exportSection.style.display = 'block';
                    // Auto-export when switching to export tab
                    if (this.onExport) {
                        // Export both ShareString and ShareLink
                        const shareString = this.onExport('sharestring');
                        const shareLink = this.onExport('sharelink');
                        
                        const shareStringEl = document.getElementById('export-sharestring');
                        const shareLinkEl = document.getElementById('export-sharelink');
                        
                        if (shareStringEl) {
                            shareStringEl.value = shareString;
                        }
                        if (shareLinkEl) {
                            shareLinkEl.value = shareLink;
                        }
                        
                        // Auto-detect WaLa from sharestring
                        this.autoDetectWaLa(shareString);
                        // Update database URLs and additional info when switching to export tab
                        await this.updateDatabaseUrls(shareString);
                    }
                }
            });
        });
        
        // Terrain selector
        const terrainSelectors = document.querySelectorAll('input[name="import-terrain"]');
        terrainSelectors.forEach(radio => {
            radio.addEventListener('change', (e) => {
                this.selectedTerrain = e.target.value;
            });
        });
        
        // Auto-detect terrain and format from input
        const importInput = document.getElementById('import-input');
        const decodeMethodSection = document.getElementById('decode-method-section');
        
        if (importInput) {
            // Function to detect format and show/hide decoding method selector
            const detectFormat = () => {
                const value = importInput.value.trim();
                
                // Show decoding method selector only for ShortLinks (1-9 alphanumeric chars, not URLs)
                if (value && 
                    !value.includes('http://') && 
                    !value.includes('https://') && 
                    !value.includes('cotgopt.com') && 
                    !value.includes('louopt.com') &&
                    !value.includes('[ShareString') &&
                    value.length >= 1 && 
                    value.length <= 9 && 
                    /^[a-zA-Z0-9]+$/.test(value) &&
                    value.length !== 4) { // Not a ShortCode (4 chars)
                    // It's likely a ShortLink
                    if (decodeMethodSection) {
                        decodeMethodSection.style.display = 'block';
                    }
                } else {
                    // Hide decoding method selector for other formats
                    if (decodeMethodSection) {
                        decodeMethodSection.style.display = 'none';
                    }
                }
                
                // Auto-detect terrain
                this.autoDetectTerrain();
            };
            
            importInput.addEventListener('paste', () => {
                // Use setTimeout to check the value after paste
                setTimeout(() => {
                    detectFormat();
                }, 10);
            });
            
            // Also check on input for manual typing
            importInput.addEventListener('input', () => {
                detectFormat();
            });
        }
        
        // Import button
        const importBtn = document.getElementById('btn-import');
        if (importBtn) {
            importBtn.addEventListener('click', async () => {
                const input = document.getElementById('import-input');
                if (!input || !input.value.trim()) {
                    return;
                }
                
                const trimmedInput = input.value.trim();
                
                // Check if it's a URL or ShortLink/ShortCode
                const isUrl = trimmedInput.includes('http://') || 
                             trimmedInput.includes('https://') || 
                             trimmedInput.includes('cotgopt.com') || 
                             trimmedInput.includes('louopt.com');
                
                const isShortCode = trimmedInput.length === 4 && /^[a-zA-Z0-9]{4}$/.test(trimmedInput);
                const isShortLink = !isUrl && 
                                   trimmedInput.length >= 1 && 
                                   trimmedInput.length <= 9 && 
                                   /^[a-zA-Z0-9]+$/.test(trimmedInput) &&
                                   !trimmedInput.includes('[ShareString') &&
                                   trimmedInput[0] !== 'W' && 
                                   trimmedInput[0] !== 'L' &&
                                   trimmedInput[0] !== 'w' && 
                                   trimmedInput[0] !== 'l';
                
                // If it's a URL, ShortCode, or ShortLink, use handleShortLinkImport
                if (isUrl || isShortCode || isShortLink) {
                    const decodeMethod = document.querySelector('input[name="decode-method"]:checked')?.value || 'og-cotg';
                    await this.handleShortLinkImport(trimmedInput, decodeMethod);
                } else {
                    // Otherwise, treat as ShareString/ShareLink
                    if (this.onImport) {
                        // Get the user's current selection (not the ShareString's designation)
                        // This allows user to override land with water if desired
                        const userSelectedTerrain = document.querySelector('input[name="import-terrain"]:checked')?.value || 'land';
                        this.selectedTerrain = userSelectedTerrain;
                        
                        // Determine ShareString and ShareLink for caching
                        let shareString = null;
                        let shareLink = null;
                        if (trimmedInput.includes('[ShareString')) {
                            shareString = trimmedInput;
                        } else if (trimmedInput.length > 0 && (trimmedInput[0] === 'W' || trimmedInput[0] === 'L' || trimmedInput[0] === 'w' || trimmedInput[0] === 'l')) {
                            shareLink = trimmedInput;
                        } else {
                            shareString = trimmedInput;
                        }
                        
                        // Cache the layout
                        if (shareString || shareLink) {
                            this.addToCache(shareString || '', shareLink || '', userSelectedTerrain, {
                                name: 'Single Import',
                                source: 'Manual import'
                            });
                        }
                        
                        // Use user's selection instead of ShareString's designation
                        this.onImport(trimmedInput, userSelectedTerrain);
                        this.close();
                    }
                }
            });
        }
        
        // Copy buttons for individual formats
        const copyFormatBtns = document.querySelectorAll('.btn-copy-format');
        copyFormatBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const targetId = btn.getAttribute('data-target');
                const targetEl = document.getElementById(targetId);
                if (targetEl) {
                    targetEl.select();
                    document.execCommand('copy');
                    const originalText = btn.textContent;
                    btn.textContent = 'Copied!';
                    btn.style.background = 'rgba(76, 175, 80, 0.3)';
                    btn.style.borderColor = '#4caf50';
                    setTimeout(() => {
                        btn.textContent = originalText;
                        btn.style.background = 'rgba(199, 168, 107, 0.2)';
                        btn.style.borderColor = 'rgba(199, 168, 107, 0.4)';
                    }, 2000);
                }
            });
        });
        
        // Note: WaLa auto-detection is handled in updateExportOutput() method
        
        // Submit layout button
        const submitLayoutBtn = document.getElementById('btn-submit-layout');
        if (submitLayoutBtn) {
            submitLayoutBtn.addEventListener('click', () => {
                this.handleSubmitLayout();
            });
        }
        
        // Bulk import buttons
        const bulkImportBtn = document.getElementById('btn-bulk-import');
        const bulkImportOnlyBtn = document.getElementById('btn-bulk-import-only');
        
        if (bulkImportBtn) {
            bulkImportBtn.addEventListener('click', () => {
                this.handleBulkImport(true); // true = save to database
            });
        }
        
        if (bulkImportOnlyBtn) {
            bulkImportOnlyBtn.addEventListener('click', () => {
                this.handleBulkImport(false); // false = don't save
            });
        }
        
        // Cached layouts buttons
        const loadCachedBtn = document.getElementById('btn-load-cached');
        const compareLayoutsBtn = document.getElementById('btn-compare-layouts');
        const clearCacheBtn = document.getElementById('btn-clear-cache');
        
        if (loadCachedBtn) {
            loadCachedBtn.addEventListener('click', () => {
                this.loadCachedLayout();
            });
        }
        
        if (compareLayoutsBtn) {
            compareLayoutsBtn.addEventListener('click', () => {
                this.openComparePage();
            });
        }
        
        if (clearCacheBtn) {
            clearCacheBtn.addEventListener('click', () => {
                if (confirm('Clear all cached layouts? This cannot be undone.')) {
                    this.clearCache();
                }
            });
        }
        
        // Setup URL copy buttons (will be set up after URLs are populated)
        this.setupUrlCopyButtons();
    }
    
    /**
     * Load a cached layout
     */
    loadCachedLayout() {
        const dropdown = document.getElementById('cached-layouts-dropdown');
        if (!dropdown || !dropdown.value) {
            alert('Please select a layout to load.');
            return;
        }
        
        const layoutId = dropdown.value;
        const layout = this.cachedLayouts.find(l => l.id.toString() === layoutId);
        
        if (!layout) {
            alert('Layout not found in cache.');
            return;
        }
        
        if (this.onImport) {
            const layoutToImport = layout.shareString || layout.shareLink;
            this.onImport(layoutToImport, layout.terrain);
            this.close();
        }
    }
    
    /**
     * Open compare page with selected layouts
     */
    openComparePage() {
        const dropdown1 = document.getElementById('cached-layouts-dropdown');
        const dropdown2 = document.getElementById('cached-layouts-dropdown-2');
        
        if (!dropdown1 || !dropdown1.value) {
            alert('Please select the first layout to compare.');
            return;
        }
        
        if (!dropdown2 || !dropdown2.value) {
            alert('Please select the second layout to compare.');
            return;
        }
        
        if (dropdown1.value === dropdown2.value) {
            alert('Please select two different layouts to compare.');
            return;
        }
        
        const layout1 = this.cachedLayouts.find(l => l.id.toString() === dropdown1.value);
        const layout2 = this.cachedLayouts.find(l => l.id.toString() === dropdown2.value);
        
        if (!layout1 || !layout2) {
            alert('One or both layouts not found in cache.');
            return;
        }
        
        // Build URL with layout data
        const basePath = this.getBasePath();
        const layout1Data = encodeURIComponent(JSON.stringify({
            shareString: layout1.shareString,
            shareLink: layout1.shareLink,
            terrain: layout1.terrain,
            name: layout1.name
        }));
        const layout2Data = encodeURIComponent(JSON.stringify({
            shareString: layout2.shareString,
            shareLink: layout2.shareLink,
            terrain: layout2.terrain,
            name: layout2.name
        }));
        
        const compareUrl = `${basePath}compare.php?layout1=${layout1Data}&layout2=${layout2Data}`;
        window.open(compareUrl, '_blank');
    }
    
    /**
     * Clear all cached layouts
     */
    clearCache() {
        this.cachedLayouts = [];
        this.saveCachedLayouts();
        
        const cachedSection = document.getElementById('cached-layouts-section');
        if (cachedSection) {
            cachedSection.style.display = 'none';
        }
        
        alert('Cache cleared.');
    }
    
    /**
     * Load cached layouts from localStorage
     */
    loadCachedLayouts() {
        try {
            const cached = localStorage.getItem('cotgopt-cached-layouts');
            if (cached) {
                this.cachedLayouts = JSON.parse(cached);
                this.updateCachedLayoutsDropdown();
                // Show cached layouts section if there are any
                const cachedSection = document.getElementById('cached-layouts-section');
                if (cachedSection) {
                    cachedSection.style.display = this.cachedLayouts.length > 0 ? 'block' : 'none';
                }
            }
        } catch (error) {
            console.error('Failed to load cached layouts:', error);
            this.cachedLayouts = [];
        }
    }
    
    /**
     * Save cached layouts to localStorage
     */
    saveCachedLayouts() {
        try {
            // Limit cache size
            if (this.cachedLayouts.length > this.maxCacheSize) {
                this.cachedLayouts = this.cachedLayouts.slice(-this.maxCacheSize);
            }
            localStorage.setItem('cotgopt-cached-layouts', JSON.stringify(this.cachedLayouts));
            this.updateCachedLayoutsDropdown();
        } catch (error) {
            console.error('Failed to save cached layouts:', error);
        }
    }
    
    /**
     * Add layout to cache
     */
    addToCache(shareString, shareLink, terrain, metadata = {}) {
        const cacheEntry = {
            id: Date.now() + Math.random(), // Unique ID
            shareString: shareString,
            shareLink: shareLink,
            terrain: terrain,
            name: metadata.name || `Layout ${this.cachedLayouts.length + 1}`,
            timestamp: Date.now(),
            ...metadata
        };
        
        // Remove duplicate if exists (same ShareString)
        this.cachedLayouts = this.cachedLayouts.filter(l => l.shareString !== shareString);
        
        // Add to beginning
        this.cachedLayouts.unshift(cacheEntry);
        
        this.saveCachedLayouts();
        
        // Show cached layouts section
        const cachedSection = document.getElementById('cached-layouts-section');
        if (cachedSection) {
            cachedSection.style.display = 'block';
        }
    }
    
    /**
     * Update cached layouts dropdown
     */
    updateCachedLayoutsDropdown() {
        const dropdown = document.getElementById('cached-layouts-dropdown');
        const dropdown2 = document.getElementById('cached-layouts-dropdown-2');
        
        if (dropdown) {
            dropdown.innerHTML = '<option value="">Select a cached layout...</option>';
            this.cachedLayouts.forEach((layout, index) => {
                const option = document.createElement('option');
                option.value = layout.id;
                option.textContent = `${layout.name} (${layout.terrain})`;
                dropdown.appendChild(option);
            });
        }
        
        if (dropdown2) {
            dropdown2.innerHTML = '<option value="">Select second layout for comparison...</option>';
            this.cachedLayouts.forEach((layout, index) => {
                const option = document.createElement('option');
                option.value = layout.id;
                option.textContent = `${layout.name} (${layout.terrain})`;
                dropdown2.appendChild(option);
            });
        }
    }
    
    /**
     * Setup copy button functionality for database URL formats
     */
    setupUrlCopyButtons() {
        // Use event delegation since URLs are dynamically populated
        const urlSection = document.getElementById('database-urls-section');
        if (urlSection) {
            urlSection.addEventListener('click', (e) => {
                const copyBtn = e.target.closest('.url-copy-btn');
                const urlCopyable = e.target.closest('.url-copyable');
                
                if (copyBtn || urlCopyable) {
                    const container = copyBtn ? copyBtn.closest('.url-copyable') : urlCopyable;
                    if (container) {
                        const url = container.getAttribute('data-url');
                        if (url) {
                            navigator.clipboard.writeText(url).then(() => {
                                // Show feedback
                                if (copyBtn) {
                                    const originalText = copyBtn.textContent;
                                    copyBtn.textContent = '✓';
                                    copyBtn.style.color = '#4caf50';
                                    setTimeout(() => {
                                        copyBtn.textContent = originalText;
                                        copyBtn.style.color = '';
                                    }, 2000);
                                } else {
                                    // If clicking the container, highlight it
                                    container.style.backgroundColor = 'rgba(76, 175, 80, 0.2)';
                                    setTimeout(() => {
                                        container.style.backgroundColor = '';
                                    }, 1000);
                                }
                            }).catch(err => {
                                console.error('Failed to copy:', err);
                                alert('Failed to copy to clipboard');
                            });
                        }
                    }
                }
            });
        }
    }
    
    /**
     * Handle bulk import of multiple layouts
     * @param {boolean} saveToDatabase - Whether to save layouts to database
     */
    async handleBulkImport(saveToDatabase) {
        if (!this.isLoggedIn) {
            alert('Please log in to use bulk upload.');
            return;
        }
        
        const bulkInput = document.getElementById('bulk-import-input');
        const progressSection = document.getElementById('bulk-import-progress');
        const statusEl = document.getElementById('bulk-import-status');
        const progressBar = document.getElementById('bulk-import-progress-bar');
        const resultsEl = document.getElementById('bulk-import-results');
        const bulkImportBtn = document.getElementById('btn-bulk-import');
        const bulkImportOnlyBtn = document.getElementById('btn-bulk-import-only');
        
        if (!bulkInput || !bulkInput.value.trim()) {
            alert('Please enter at least one layout to import.');
            return;
        }
        
        // Get all lines (non-empty)
        const lines = bulkInput.value.trim().split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0);
        
        if (lines.length === 0) {
            alert('Please enter at least one layout to import.');
            return;
        }
        
        // Show progress section
        if (progressSection) {
            progressSection.style.display = 'block';
        }
        if (statusEl) {
            statusEl.textContent = `Processing ${lines.length} layout(s)...`;
        }
        if (progressBar) {
            progressBar.style.width = '0%';
        }
        if (resultsEl) {
            resultsEl.innerHTML = '';
        }
        
        // Disable buttons
        if (bulkImportBtn) bulkImportBtn.disabled = true;
        if (bulkImportOnlyBtn) bulkImportOnlyBtn.disabled = true;
        
        const results = {
            success: [],
            failed: []
        };
        
        const basePath = this.getBasePath();
        const apiBase = `${basePath}api/layouts/`;
        
        try {
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                
                // Update progress
                const progress = ((i + 1) / lines.length) * 100;
                if (progressBar) {
                    progressBar.style.width = `${progress}%`;
                }
                if (statusEl) {
                    statusEl.textContent = `Processing ${i + 1} of ${lines.length}...`;
                }
                
                try {
                    // Detect format and get ShareString
                    let shareString = null;
                    let shareLink = null;
                    let terrain = 'land';
                    
                    const isUrl = line.includes('http://') || 
                                 line.includes('https://') || 
                                 line.includes('cotgopt.com') || 
                                 line.includes('louopt.com');
                    
                    const isShortCode = line.length === 4 && /^[a-zA-Z0-9]{4}$/.test(line);
                    const isShortLink = !isUrl && 
                                       line.length >= 1 && 
                                       line.length <= 9 && 
                                       /^[a-zA-Z0-9]+$/.test(line) &&
                                       !line.includes('[ShareString') &&
                                       line[0] !== 'W' && 
                                       line[0] !== 'L' &&
                                       line[0] !== 'w' && 
                                       line[0] !== 'l';
                    
                    if (isUrl || isShortCode || isShortLink) {
                        // Handle as short link/URL - fetch from API
                        const decodeMethod = document.querySelector('input[name="decode-method"]:checked')?.value || 'og-cotg';
                        const layoutData = await this.fetchLayoutFromShortLink(line, decodeMethod, apiBase);
                        
                        if (layoutData && layoutData.layout) {
                            shareString = layoutData.layout.Sharestring || layoutData.layout.ShareString;
                            shareLink = layoutData.layout.LongLink || layoutData.layout.ShareLink;
                            terrain = layoutData.layout.WaLa === 'Water' ? 'water' : 'land';
                        } else {
                            results.failed.push({ line: line.substring(0, 50), error: 'Layout not found' });
                            continue;
                        }
                    } else {
                        // Handle as ShareString/ShareLink
                        if (line.includes('[ShareString')) {
                            shareString = line;
                            const match = /\[ShareString\.1\.3\]([^[]*)/.exec(line);
                            if (match && match[1]) {
                                terrain = match[1].charAt(0) === ';' ? 'water' : 'land';
                            }
                        } else if (line.length > 0 && (line[0] === 'W' || line[0] === 'L' || line[0] === 'w' || line[0] === 'l')) {
                            shareLink = line;
                            terrain = (line[0] === 'W' || line[0] === 'w') ? 'water' : 'land';
                            // For ShareLink, we need to load it first to get ShareString
                            if (this.onImport && saveToDatabase) {
                                this.onImport(shareLink, terrain);
                                await new Promise(resolve => setTimeout(resolve, 200));
                                if (this.onExport) {
                                    shareString = this.onExport('sharestring');
                                }
                            }
                        } else {
                            results.failed.push({ line: line.substring(0, 50), error: 'Unknown format' });
                            continue;
                        }
                    }
                    
                    if (!shareString && !saveToDatabase) {
                        // For import-only, we can use ShareLink directly
                        if (shareLink && this.onImport) {
                            this.onImport(shareLink, terrain);
                            results.success.push({ 
                                line: line.substring(0, 50) + (line.length > 50 ? '...' : ''),
                                action: 'Imported'
                            });
                            continue;
                        }
                    }
                    
                    if (!shareString) {
                        results.failed.push({ line: line.substring(0, 50), error: 'Could not get ShareString' });
                        continue;
                    }
                    
                    // If saving to database, check if layout exists and handle accordingly
                    if (saveToDatabase) {
                        try {
                            // Check if "Set to Favorites" is enabled
                            const setToFavorites = document.getElementById('bulk-set-favorites')?.checked || false;
                            
                            // Search for existing layout by ShareString
                            let existingLayout = null;
                            if (setToFavorites) {
                                try {
                                    const searchResponse = await fetch(`${apiBase}search.php?q=${encodeURIComponent(shareString)}&field=Sharestring&limit=10`);
                                    const searchData = await searchResponse.json();
                                    if (searchData.success && searchData.results && searchData.results.length > 0) {
                                        // Check if exact match
                                        const found = searchData.results.find(l => {
                                            const layoutShareString = l.Sharestring || l.ShareString || '';
                                            return layoutShareString.trim() === shareString.trim();
                                        });
                                        if (found) {
                                            existingLayout = found;
                                        }
                                    }
                                } catch (error) {
                                    console.warn('Error searching for existing layout:', error);
                                }
                            }
                            
                            if (existingLayout && setToFavorites) {
                                // Layout exists - add to favorites instead
                                try {
                                    const favoriteResponse = await fetch(`${apiBase}favorites.php`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({ layout_id: existingLayout.id })
                                    });
                                    
                                    const favoriteResult = await favoriteResponse.json();
                                    
                                    if (favoriteResult.success) {
                                        results.success.push({ 
                                            line: line.substring(0, 50) + (line.length > 50 ? '...' : ''),
                                            action: favoriteResult.message && favoriteResult.message.includes('Already') ? 'Already in Favorites' : 'Added to Favorites',
                                            layoutName: existingLayout.LayoutName || 'Existing Layout',
                                            id: existingLayout.id
                                        });
                                    } else {
                                        results.failed.push({ line: line.substring(0, 50), error: favoriteResult.error || 'Failed to add to favorites' });
                                    }
                                } catch (error) {
                                    results.failed.push({ line: line.substring(0, 50), error: error.message || 'Favorite error' });
                                }
                            } else {
                                // Layout doesn't exist or favorites not enabled - save as new
                                const submitData = {
                                    LayoutName: `Bulk Import ${i + 1}`,
                                    Sharestring: shareString,
                                    LongLink: shareLink || (this.onExport ? this.onExport('sharelink') : ''),
                                    Creator: this.currentUsername || 'Unknown',
                                    WaLa: terrain === 'water' ? 'Water' : 'Land',
                                    mapType: 'Bulk Import'
                                };
                                
                                const submitResponse = await fetch(`${apiBase}submit.php`, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify(submitData)
                                });
                                
                                const submitResult = await submitResponse.json();
                                
                                if (submitResult.success) {
                                    results.success.push({ 
                                        line: line.substring(0, 50) + (line.length > 50 ? '...' : ''),
                                        action: 'Saved',
                                        layoutName: submitResult.layout?.LayoutName || 'Bulk Import',
                                        id: submitResult.layout?.id
                                    });
                                } else {
                                    results.failed.push({ line: line.substring(0, 50), error: submitResult.error || 'Save failed' });
                                }
                            }
                        } catch (error) {
                            results.failed.push({ line: line.substring(0, 50), error: error.message || 'Save error' });
                        }
                    } else {
                        // Just import, don't save - cache the layout for later access
                        if (this.onImport) {
                            const layoutToImport = shareString || shareLink;
                            
                            // Cache the layout
                            this.addToCache(shareString, shareLink, terrain, {
                                name: `Bulk Import ${i + 1}`,
                                source: line.substring(0, 50)
                            });
                            
                            // Load the last one into the canvas
                            if (i === lines.length - 1) {
                                this.onImport(layoutToImport, terrain);
                            }
                            
                            results.success.push({ 
                                line: line.substring(0, 50) + (line.length > 50 ? '...' : ''),
                                action: 'Cached',
                                index: i + 1
                            });
                        }
                    }
                } catch (error) {
                    results.failed.push({ line: line.substring(0, 50), error: error.message || 'Import failed' });
                }
            }
            
            // Update final status
            if (statusEl) {
                statusEl.textContent = `Completed: ${results.success.length} success, ${results.failed.length} failed`;
            }
            if (progressBar) {
                progressBar.style.width = '100%';
            }
            
            // Display results
            if (resultsEl) {
                let resultsHTML = '';
                
                if (results.success.length > 0) {
                    resultsHTML += `<div style="color: #4caf50; margin-bottom: 10px;"><strong>Success (${results.success.length}):</strong></div>`;
                    results.success.forEach((result, idx) => {
                        resultsHTML += `<div style="font-size: 11px; margin-left: 10px; margin-bottom: 4px;">
                            ${idx + 1}. ${result.layoutName || result.action}${result.id ? ` (ID: ${result.id})` : ''}
                        </div>`;
                    });
                }
                
                if (results.failed.length > 0) {
                    resultsHTML += `<div style="color: #f44336; margin-top: 15px; margin-bottom: 10px;"><strong>Failed (${results.failed.length}):</strong></div>`;
                    results.failed.forEach((result, idx) => {
                        resultsHTML += `<div style="font-size: 11px; margin-left: 10px; margin-bottom: 4px;">
                            ${idx + 1}. ${result.line}${result.line.length < 50 ? '' : '...'} - ${result.error}
                        </div>`;
                    });
                }
                
                resultsEl.innerHTML = resultsHTML;
            }
            
        } catch (error) {
            console.error('Bulk import error:', error);
            if (statusEl) {
                statusEl.textContent = `Error: ${error.message}`;
            }
        } finally {
            // Re-enable buttons
            if (bulkImportBtn) bulkImportBtn.disabled = false;
            if (bulkImportOnlyBtn) bulkImportOnlyBtn.disabled = false;
        }
    }
    
    /**
     * Fetch layout from short link/URL (helper for bulk import)
     */
    async fetchLayoutFromShortLink(input, decodeMethod, apiBase) {
        const trimmedInput = input.trim();
        
        // Extract code from URL if needed
        let shortCode = trimmedInput;
        if (trimmedInput.includes('http://') || trimmedInput.includes('https://')) {
            const urlPatterns = [
                /\/api\/(maps|layouts)\/short\/([^\/\?]+)/i,
                /\/([A-Za-z0-9]{1,9})(?:\?|$|\/)/i
            ];
            
            for (const pattern of urlPatterns) {
                const match = trimmedInput.match(pattern);
                if (match) {
                    shortCode = match[match.length - 1];
                    break;
                }
            }
        }
        
        // Try decode endpoint
        try {
            const decodeResponse = await fetch(`${apiBase}decode.php?code=${encodeURIComponent(shortCode)}&method=${decodeMethod}`);
            const decodeData = await decodeResponse.json();
            
            if (decodeData.success && decodeData.found && decodeData.layout) {
                return decodeData;
            }
            
            // Try other method if first failed
            const otherMethod = decodeMethod === 'og-cotg' ? 'lou' : 'og-cotg';
            const decodeResponse2 = await fetch(`${apiBase}decode.php?code=${encodeURIComponent(shortCode)}&method=${otherMethod}`);
            const decodeData2 = await decodeResponse2.json();
            
            if (decodeData2.success && decodeData2.found && decodeData2.layout) {
                return decodeData2;
            }
        } catch (e) {
            console.log('Decode failed:', e);
        }
        
        return null;
    }
    
    /**
     * Handle submitting layout to database
     */
    async handleSubmitLayout() {
        const submitBtn = document.getElementById('btn-submit-layout');
        const messageEl = document.getElementById('submit-layout-message');
        const shareStringEl = document.getElementById('export-sharestring');
        
        if (!shareStringEl || !shareStringEl.value.trim()) {
            this.showSubmitMessage('error', 'Please export your city first!');
            return;
        }
        
        // Get form values
        const layoutName = document.getElementById('submit-layout-name')?.value.trim();
        const creator = document.getElementById('submit-layout-creator')?.value.trim();
        const shortLink = document.getElementById('submit-layout-shortlink')?.value.trim();
        const mapType = document.getElementById('submit-layout-maptype')?.value.trim();
        const wala = document.getElementById('submit-layout-wala')?.value;
        
        // Get the exported values
        const shareString = shareStringEl.value.trim();
        const shareLinkEl = document.getElementById('export-sharelink');
        const shareLink = shareLinkEl ? shareLinkEl.value.trim() : '';
        
        // Use the values directly from the export fields
        const sharestring = shareString;
        const longLink = shareLink;
        
        // Validate required fields
        if (!layoutName) {
            this.showSubmitMessage('error', 'Layout Name is required!');
            return;
        }
        
        // Disable button and show loading
        submitBtn.disabled = true;
        submitBtn.textContent = 'Submitting...';
        messageEl.style.display = 'none';
        
        try {
            // Determine API base path (try to get from current page or use relative path)
            const basePath = this.getBasePath();
            const apiBase = `${basePath}api/layouts/`;
            
            const response = await fetch(`${apiBase}submit.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    LayoutName: layoutName,
                    Sharestring: sharestring,
                    Creator: creator || '',
                    ShortLink: shortLink || '',
                    LongLink: longLink || '',
                    mapType: mapType || '',
                    WaLa: wala || ''
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                this.showSubmitMessage('success', `Layout submitted successfully! (ID: ${result.layout?.id || 'N/A'})`);
                // Clear form (except sharestring)
                document.getElementById('submit-layout-name').value = '';
                document.getElementById('submit-layout-creator').value = '';
                document.getElementById('submit-layout-shortlink').value = '';
                document.getElementById('submit-layout-maptype').value = '';
                document.getElementById('submit-layout-wala').value = '';
            } else {
                this.showSubmitMessage('error', result.error || result.message || 'Submission failed');
            }
        } catch (error) {
            this.showSubmitMessage('error', 'Error submitting layout: ' + error.message);
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit to Database';
        }
    }
    
    /**
     * Show message in submit layout section
     */
    showSubmitMessage(type, message) {
        const messageEl = document.getElementById('submit-layout-message');
        if (!messageEl) return;
        
        messageEl.style.display = 'block';
        messageEl.style.background = type === 'success' 
            ? 'rgba(40, 167, 69, 0.2)' 
            : 'rgba(220, 53, 69, 0.2)';
        messageEl.style.border = `1px solid ${type === 'success' ? '#28a745' : '#dc3545'}`;
        messageEl.style.color = type === 'success' ? '#90ee90' : '#ff6b6b';
        messageEl.textContent = message;
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            messageEl.style.display = 'none';
        }, 5000);
    }
    
    /**
     * Get base path for API calls
     */
    getBasePath() {
        // Get current path and hostname
        let path = window.location.pathname;
        const hostname = window.location.hostname;
        
        // Check for root domains: cotg.rlmglobal.com or cotgopt.com (base path is root)
        if (hostname.includes('cotg.rlmglobal.com') || 
            (hostname.includes('cotgopt.com') && !path.includes('/cotgopt/'))) {
            return '/';
        }
        
        // Remove index.php or other PHP files from the path
        path = path.replace(/\/[^\/]+\.php$/, '');
        
        // Check for common base paths
        if (path.includes('/Louopt')) {
            return '/Louopt/';
        }
        if (path.includes('/cotgopt')) {
            return '/cotgopt/';
        }
        if (path.includes('/cotg/')) {
            // rlmglobal.com/cotg/ - subdirectory
            return '/cotg/';
        }
        
        // Try to extract base from current location
        // If path is just '/' or empty, return '/'
        if (path === '/' || path === '') {
            return '/';
        }
        
        // If path ends with index.html, remove it
        if (path.endsWith('/index.html') || path.endsWith('index.html')) {
            path = path.replace(/\/?index\.html$/, '');
        }
        
        // If path is now empty or just '/', return '/'
        if (path === '' || path === '/') {
            return '/';
        }
        
        // If we're at /Louopt/index.php, base is /Louopt/
        const pathParts = path.split('/').filter(p => p);
        if (pathParts.length > 0) {
            // Assume the first directory is the base
            return '/' + pathParts[0] + '/';
        }
        
        // Default to root
        return '/';
    }
    
    /**
     * Handle importing by short link or URL
     */
    async handleShortLinkImport(input, decodeMethod = 'og-cotg') {
        try {
            // Extract short code from input
            let shortCode = input.trim();
            
            // If it's a URL, extract the code
            if (shortCode.includes('http://') || shortCode.includes('https://')) {
                // Extract code from URL patterns:
                // http://cotgopt.com/J0cs
                // http://cotgopt.com/api/maps/short/J0cs
                // http://cotgopt.com/api/layouts/short/J0cs
                const urlPatterns = [
                    /\/api\/(maps|layouts)\/short\/([^\/\?]+)/i,
                    /\/([A-Za-z0-9]{1,9})(?:\?|$|\/)/i
                ];
                
                let found = false;
                for (const pattern of urlPatterns) {
                    const match = shortCode.match(pattern);
                    if (match) {
                        shortCode = match[match.length - 1]; // Get the last capture group
                        found = true;
                        break;
                    }
                }
                
                if (!found) {
                    // Try to extract from end of URL
                    const parts = shortCode.split('/').filter(p => p);
                    if (parts.length > 0) {
                        const lastPart = parts[parts.length - 1].split('?')[0];
                        if (lastPart.length >= 1 && lastPart.length <= 9) {
                            shortCode = lastPart;
                        }
                    }
                }
            }
            
            // Validate shortcode format (base-62 characters, 1-9 characters)
            if (!shortCode || !/^[a-zA-Z0-9]{1,9}$/.test(shortCode)) {
                alert('Invalid short link format. Please enter a 1-9 character code (a-z, A-Z, 0-9) or a valid URL.');
                return;
            }
            
            // Show loading state
            const importBtn = document.getElementById('btn-import');
            const originalText = importBtn?.textContent || 'Import';
            if (importBtn) {
                // Store original text as data attribute for recovery
                importBtn.setAttribute('data-original-text', originalText);
                importBtn.disabled = true;
                importBtn.textContent = 'Loading...';
            }
            
            // Get base path (same method as submit function)
            const basePath = this.getBasePath();
            const apiBase = `${basePath}api/layouts/`;
            
            // Use the decode endpoint with the selected method
            const apiUrl = `${apiBase}decode.php?code=${encodeURIComponent(shortCode)}&method=${encodeURIComponent(decodeMethod)}`;
            
            console.log('Decoding short link from:', apiUrl, 'Method:', decodeMethod, 'Base path:', basePath);
            
            // Decode shortlink and get layout
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });
            
            // Check content type first
            const contentType = response.headers.get('content-type') || '';
            const isJson = contentType.includes('application/json');
            
            // Get response text (we'll parse it based on content type)
            const responseText = await response.text();
            
            if (!response.ok) {
                // Try to get error message from response
                let errorMsg = `Failed to decode short link: ${response.status} ${response.statusText}`;
                if (isJson) {
                    try {
                        const errorJson = JSON.parse(responseText);
                        errorMsg = errorJson.error || errorJson.message || errorMsg;
                        
                        // If not found with current method, suggest trying the other method
                        if (response.status === 404 && errorJson.found === false) {
                            const otherMethod = decodeMethod === 'og-cotg' ? 'Lou' : 'OG-CoTG';
                            errorMsg += `\n\nTry switching to "${otherMethod}" decoding method if this is an older shortlink.`;
                        }
                    } catch (e) {
                        // JSON parse failed, use default
                    }
                } else {
                    // Check if it's HTML (error page)
                    if (responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html')) {
                        errorMsg = `Server error: The API returned an HTML error page (likely a PHP error). Status: ${response.status}`;
                    } else {
                        errorMsg = `Server error: ${response.status} ${response.statusText}. Response: ${responseText.substring(0, 200)}`;
                    }
                }
                alert(errorMsg);
                if (importBtn) {
                    importBtn.disabled = false;
                    importBtn.textContent = originalText;
                }
                return;
            }
            
            // Parse JSON response
            if (!isJson) {
                throw new Error(`Expected JSON but got ${contentType}. URL: ${apiUrl}. Response: ${responseText.substring(0, 200)}`);
            }
            
            let result;
            try {
                result = JSON.parse(responseText);
            } catch (e) {
                throw new Error(`Failed to parse JSON response: ${e.message}. URL: ${apiUrl}. Response: ${responseText.substring(0, 200)}`);
            }
            
            if (!result.success || !result.found || !result.layout) {
                const errorMsg = result.error || `Layout not found for short link: ${shortCode} (decoded ID: ${result.decoded_id || 'N/A'}, method: ${result.method || decodeMethod})`;
                const otherMethod = decodeMethod === 'og-cotg' ? 'Lou' : 'OG-CoTG';
                alert(`${errorMsg}\n\nTry switching to "${otherMethod}" decoding method if this is an older shortlink.`);
                if (importBtn) {
                    importBtn.disabled = false;
                    importBtn.textContent = originalText;
                }
                return;
            }
            
            const layout = result.layout;
            
            // Call autofill to update database if fields are missing (same as search area)
            if (layout.id) {
                try {
                    const autofillResponse = await fetch(`${apiBase}autofill.php`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ id: layout.id })
                    });
                    // Don't fail if autofill fails, just continue
                    if (autofillResponse.ok) {
                        const autofillData = await autofillResponse.json();
                        if (autofillData.success && autofillData.updated) {
                            // Update layout with autofilled data if available
                            if (autofillData.layout) {
                                Object.assign(layout, autofillData.layout);
                            }
                        }
                    }
                } catch (e) {
                    console.log('Auto-fill failed, continuing with load:', e);
                }
            }
            
            // Prefer LongLink over Sharestring (LongLink is shorter ShareLink format)
            let layoutString = null;
            if (layout.LongLink && layout.LongLink.trim()) {
                layoutString = layout.LongLink.trim();
            } else if (layout.Sharestring && layout.Sharestring.trim()) {
                layoutString = layout.Sharestring.trim();
            }
            
            if (!layoutString) {
                alert('Layout found but no LongLink or ShareString available.');
                if (importBtn) {
                    importBtn.disabled = false;
                    importBtn.textContent = originalText;
                }
                return;
            }
            
            // Auto-detect terrain from WaLa field if available
            let terrain = null;
            if (layout.WaLa) {
                terrain = layout.WaLa.toLowerCase() === 'water' ? 'water' : 'land';
                // Update terrain selector
                const terrainRadio = document.querySelector(`input[name="import-terrain"][value="${terrain}"]`);
                if (terrainRadio) {
                    terrainRadio.checked = true;
                    this.selectedTerrain = terrain;
                }
            }
            
            // Determine ShareString and ShareLink for caching
            let shareString = null;
            let shareLink = null;
            if (layoutString.includes('[ShareString')) {
                shareString = layoutString;
            } else {
                shareLink = layoutString;
                // Try to get ShareString from layout
                if (layout.Sharestring) {
                    shareString = layout.Sharestring;
                }
            }
            
            // Cache the layout
            this.addToCache(shareString || '', shareLink || '', terrain || 'land', {
                name: layout.LayoutName || `ShortLink: ${shortCode}`,
                source: 'ShortLink import',
                shortCode: shortCode
            });
            
            // Import the layout string (prefer LongLink, fallback to Sharestring)
            if (this.onImport) {
                // Pass the layout string (LongLink or Sharestring) so we can populate City Information
                // The onImport handler will handle the import, and we'll populate metadata separately
                this.onImport(layoutString, terrain);
            }
            
            // Reset button state and clear input so user can load another shortlink
            // Keep modal open so user can load multiple shortlinks in sequence
            if (importBtn) {
                importBtn.disabled = false;
                importBtn.textContent = originalText;
            }
            
            // Clear the input field so user can enter another shortlink
            const importInput = document.getElementById('import-input');
            if (importInput) {
                importInput.value = '';
                // Focus the input so user can immediately type another shortlink
                importInput.focus();
            }
            
            // Show success message (optional, can be removed if too intrusive)
            // The layout has been loaded, modal stays open for next import
            
        } catch (error) {
            alert('Failed to load layout: ' + error.message);
            console.error('Short link import error:', error);
            const importBtn = document.getElementById('btn-import');
            if (importBtn) {
                importBtn.disabled = false;
                // Try to restore original text, fallback to 'Import'
                const originalText = importBtn.getAttribute('data-original-text') || 'Import';
                importBtn.textContent = originalText;
            }
        } finally {
            // Always ensure button is enabled, even if something went wrong
            const importBtn = document.getElementById('btn-import');
            if (importBtn && importBtn.disabled) {
                const originalText = importBtn.getAttribute('data-original-text') || 'Import';
                importBtn.disabled = false;
                importBtn.textContent = originalText;
            }
        }
    }
    
    /**
     * Auto-detect WaLa (Water/Land) from ShareString
     */
    autoDetectWaLa(sharestring) {
        if (!sharestring) return;
        
        const walaSelect = document.getElementById('submit-layout-wala');
        if (!walaSelect || walaSelect.value) return; // Don't override if already set
        
        // Check if ShareString indicates water or land
        if (sharestring.includes('[ShareString.1.3]')) {
            const match = /\[ShareString\.1\.3\]([^[]*)/.exec(sharestring);
            if (match && match[1]) {
                const firstChar = match[1].charAt(0);
                if (firstChar === ';') {
                    walaSelect.value = 'Water';
                } else if (firstChar === ':') {
                    walaSelect.value = 'Land';
                }
            }
        }
    }
    
    /**
     * Auto-detect terrain from ShareString format
     */
    autoDetectTerrain() {
        const input = document.getElementById('import-input');
        if (!input) return;
        
        const text = input.value.trim();
        let detectedTerrain = null;
        
        // Check if ShareString starts with semicolon (water) or colon (land)
        if (text.includes('[ShareString.1.3]')) {
            // Extract the character after [ShareString.1.3]
            const match = /\[ShareString\.1\.3\]([^[]*)/.exec(text);
            if (match && match[1]) {
                const firstChar = match[1].charAt(0);
                if (firstChar === ';') {
                    detectedTerrain = 'water';
                } else if (firstChar === ':') {
                    detectedTerrain = 'land';
                }
            }
        } else if (text.trim().length > 0) {
            // Check ShareLink format (W = water, L = land)
            const firstChar = text.trim().charAt(0);
            if (firstChar === 'W' || firstChar === 'w') {
                detectedTerrain = 'water';
            } else if (firstChar === 'L' || firstChar === 'l') {
                detectedTerrain = 'land';
            }
        }
        
        // Apply auto-detection with override logic
        if (detectedTerrain) {
            if (detectedTerrain === 'water') {
                // Water: always update selector (no override allowed)
                this.selectedTerrain = 'water';
                const waterRadio = document.querySelector('input[name="import-terrain"][value="water"]');
                if (waterRadio) {
                    waterRadio.checked = true;
                }
            } else if (detectedTerrain === 'land') {
                // Land: update selector only if user hasn't manually selected water
                // This allows user to override land with water if they wish
                const currentSelection = document.querySelector('input[name="import-terrain"]:checked')?.value;
                if (currentSelection !== 'water') {
                    // User hasn't selected water, so update to land from ShareString
                    this.selectedTerrain = 'land';
                    const landRadio = document.querySelector('input[name="import-terrain"][value="land"]');
                    if (landRadio) {
                        landRadio.checked = true;
                    }
                }
                // If user has already selected water, keep their selection (don't override)
            }
        }
    }
    
    /**
     * Update export output
     */
    async updateExportOutput(text) {
        // Update both ShareString and ShareLink fields
        const shareStringEl = document.getElementById('export-sharestring');
        const shareLinkEl = document.getElementById('export-sharelink');
        
        if (this.onExport) {
            const shareString = this.onExport('sharestring');
            const shareLink = this.onExport('sharelink');
            
            if (shareStringEl) {
                shareStringEl.value = shareString;
            }
            if (shareLinkEl) {
                shareLinkEl.value = shareLink;
            }
            
            // Auto-detect WaLa from the exported sharestring
            this.autoDetectWaLa(shareString);
            
            // Search for layout in database and show URL formats if found
            await this.updateDatabaseUrls(shareString);
        }
    }
    
    /**
     * Search for layout in database and update URL formats section
     */
    async updateDatabaseUrls(layoutString) {
        if (!layoutString || !layoutString.trim()) {
            // Hide URL section if no layout string
            const urlSection = document.getElementById('database-urls-section');
            if (urlSection) {
                urlSection.style.display = 'none';
            }
            return;
        }
        
        try {
            // Determine API base path
            const basePath = this.getBasePath();
            const apiBase = `${basePath}api/layouts/`;
            
            // Determine if it's ShareString or ShareLink format
            let sharestring = null;
            let longLink = null;
            
            if (layoutString.includes('[ShareString')) {
                // It's a ShareString format
                sharestring = layoutString.trim();
            } else if (layoutString.length > 0 && (layoutString[0] === 'W' || layoutString[0] === 'L')) {
                // It's a ShareLink format
                longLink = layoutString.trim();
            }
            
            // Use POST for long ShareStrings to avoid URL length limits (2000 chars)
            const usePost = sharestring && sharestring.length > 2000;
            
            let response;
            if (usePost) {
                // Use POST for long strings
                response = await fetch(`${apiBase}find.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        sharestring: sharestring || '',
                        longLink: longLink || ''
                    })
                });
            } else {
                // Use GET for short strings
                const params = new URLSearchParams();
                if (sharestring) {
                    params.append('sharestring', sharestring);
                }
                if (longLink) {
                    params.append('longLink', longLink);
                }
                
                if (!params.toString()) {
                    // Can't search, hide section
                    const urlSection = document.getElementById('database-urls-section');
                    if (urlSection) {
                        urlSection.style.display = 'none';
                    }
                    return;
                }
                
                response = await fetch(`${apiBase}find.php?${params}`);
            }
            
            const data = await response.json();
            
            const urlSection = document.getElementById('database-urls-section');
            if (!urlSection) return;
            
            if (data.success && data.found && data.layout) {
                const layout = data.layout;
                const baseUrl = this.getBaseUrl();
                
                // Update ShortLink and ShortCode sections
                const shortlinkShortcodeSection = document.getElementById('shortlink-shortcode-section');
                const shortLinkContainer = document.getElementById('export-shortlink-container');
                const shortCodeContainer = document.getElementById('export-shortcode-container');
                
                // Show ShortLink if available
                if (layout.ShortLink && shortLinkContainer) {
                    const shortLinkInput = document.getElementById('export-shortlink');
                    if (shortLinkInput) {
                        shortLinkInput.value = layout.ShortLink;
                        shortLinkContainer.style.display = 'block';
                    }
                } else if (shortLinkContainer) {
                    shortLinkContainer.style.display = 'none';
                }
                
                // Show ShortCode if available (4 characters)
                if (layout.ShortCode && shortCodeContainer) {
                    const shortCodeInput = document.getElementById('export-shortcode');
                    if (shortCodeInput) {
                        shortCodeInput.value = layout.ShortCode;
                        shortCodeContainer.style.display = 'block';
                    }
                } else if (shortCodeContainer) {
                    shortCodeContainer.style.display = 'none';
                }
                
                // Show the section if at least one is available
                if (shortlinkShortcodeSection) {
                    const hasShortLink = shortLinkContainer && shortLinkContainer.style.display === 'block';
                    const hasShortCode = shortCodeContainer && shortCodeContainer.style.display === 'block';
                    shortlinkShortcodeSection.style.display = (hasShortLink || hasShortCode) ? 'block' : 'none';
                }
                
                // Update Additional Information section
                const additionalInfoSection = document.getElementById('additional-info-section');
                const additionalInfoContent = document.getElementById('additional-info-content');
                
                if (additionalInfoSection && additionalInfoContent) {
                    let hasInfo = false;
                    let infoHTML = '';
                    
                    // Troop Numbers
                    if (layout.TroopNumbers) {
                        try {
                            const troopNumbers = JSON.parse(layout.TroopNumbers);
                            if (Array.isArray(troopNumbers) && troopNumbers.some(count => count > 0)) {
                                const totalTroops = troopNumbers.reduce((sum, count) => sum + (count || 0), 0);
                                infoHTML += `<div><strong>Total Troops:</strong> ${totalTroops.toLocaleString()}</div>`;
                                hasInfo = true;
                            }
                        } catch (e) {
                            console.log('Failed to parse troop numbers:', e);
                        }
                    }
                    
                    // Layout Resources
                    if (layout.LayoutResources) {
                        try {
                            const resources = JSON.parse(layout.LayoutResources);
                            if (Array.isArray(resources) && resources.length >= 23) {
                                const resourceNames = ['Wood', 'Stone', 'Iron', 'Food', 'Gold', 'Silver', 'Copper', 'Tin', 'Lead', 'Coal', 'Oil', 'Uranium', 'Gems', 'Crystals', 'Diamonds', 'Platinum', 'Titanium', 'Mercury', 'Sulfur', 'Salt', 'Marble', 'Obsidian', 'Crystal'];
                                let resourceInfo = '<div><strong>Resources:</strong><ul style="margin: 4px 0; padding-left: 20px;">';
                                resources.forEach((amount, index) => {
                                    if (amount > 0 && resourceNames[index]) {
                                        resourceInfo += `<li>${resourceNames[index]}: ${amount.toLocaleString()}</li>`;
                                    }
                                });
                                resourceInfo += '</ul></div>';
                                infoHTML += resourceInfo;
                                hasInfo = true;
                            }
                        } catch (e) {
                            console.log('Failed to parse layout resources:', e);
                        }
                    }
                    
                    // Layout Name
                    if (layout.LayoutName) {
                        infoHTML += `<div><strong>Layout Name:</strong> ${layout.LayoutName}</div>`;
                        hasInfo = true;
                    }
                    
                    // Creator
                    if (layout.Creator) {
                        infoHTML += `<div><strong>Creator:</strong> ${layout.Creator}</div>`;
                        hasInfo = true;
                    }
                    
                    // Map Type
                    if (layout.MapType) {
                        infoHTML += `<div><strong>Map Type:</strong> ${layout.MapType}</div>`;
                        hasInfo = true;
                    }
                    
                    // WaLa
                    if (layout.WaLa) {
                        infoHTML += `<div><strong>Terrain:</strong> ${layout.WaLa}</div>`;
                        hasInfo = true;
                    }
                    
                    if (hasInfo) {
                        additionalInfoContent.innerHTML = infoHTML;
                        additionalInfoSection.style.display = 'block';
                    } else {
                        additionalInfoSection.style.display = 'none';
                    }
                }
                
                // Hide old database-urls-section (we're using the new layout now)
                if (urlSection) {
                    urlSection.style.display = 'none';
                }
            } else {
                // Layout not found - hide all sections
                const shortlinkShortcodeSection = document.getElementById('shortlink-shortcode-section');
                const additionalInfoSection = document.getElementById('additional-info-section');
                if (shortlinkShortcodeSection) shortlinkShortcodeSection.style.display = 'none';
                if (additionalInfoSection) additionalInfoSection.style.display = 'none';
                if (urlSection) urlSection.style.display = 'none';
            }
        } catch (error) {
            console.log('Failed to search for layout URLs:', error);
            // Hide section on error
            const urlSection = document.getElementById('database-urls-section');
            if (urlSection) {
                urlSection.style.display = 'none';
            }
        }
    }
    
    /**
     * Get base URL (cotgopt.com or Louopt based on current path)
     */
    getBaseUrl() {
        const hostname = window.location.hostname;
        const path = window.location.pathname;
        
        // Determine if we're on cotgopt or Louopt
        if (path.includes('/cotgopt/')) {
            return hostname.includes('localhost') ? 'http://localhost/cotgopt/' : 'https://cotgopt.com/';
        } else if (path.includes('/Louopt/')) {
            return hostname.includes('localhost') ? 'http://localhost/Louopt/' : 'https://cotgopt.com/Louopt/';
        } else {
            // Default to cotgopt.com
            return hostname.includes('localhost') ? 'http://localhost/' : 'https://cotgopt.com/';
        }
    }
    
    /**
     * Truncate text with ellipsis
     */
    truncateText(text, maxLength) {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength);
    }
}

